// File: src/pages/SessionsPage.jsx

import React, { useState, useContext, useCallback, useMemo } from 'react';
import {
  Page,
  Layout,
  Card,
  DataTable,
  Banner,
  SkeletonPage,
  SkeletonBodyText,
  Text,
  Button,
  Box,
  List,
  Modal,
  Spinner,
  InlineStack,
  Divider,
  Scrollable,
  Tooltip,
  Icon,
} from '@shopify/polaris';
import { QuestionCircleIcon, ArrowLeftIcon } from '@shopify/polaris-icons';
import { AuthContext } from '../context/AuthContext';
import { useDataCache } from '../context/DataCacheProvider';
import LayoutWrapper from '../components/Layout/Layout';
import useData from '../hooks/useData';
import '../components/Layout/ui-shell.css';
import ReactMarkdown from 'react-markdown';
import { cleanTextNoMarkdown} from '../utils/cleanText';
import PrescriptionBanner from '../components/Prescriptions/PrescriptionBanner';
// Recharts imports for the scatter plot
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
} from 'recharts';

// Replacements for cleaning AI-generated text. Applied only at render time.
const REPLACEMENTS = [
  [/\b(call-to-action)\s*\((call-to-action)\)/gi, '$1'],
  [/\b(?:DIV|SPAN|SECTION|CONTAINER|WRAPPER|CARD)\b/gi, 'section'],
  [/\b(?:INPUT|TEXTAREA|SELECT|FIELD|FORM[-\s]?FIELD)\b/gi, 'form field'],
  [/\b(?:BUTTON|BTN)\b/gi, 'button'],
  [/\bid=["'][^"']*["']/gi, ''],
  [/\bdata-[\w-]+=["'][^"']*["']/gi, ''],
  [/\s{2,}/g, ' '],
  // NO MARKDOWN STRIPPING HERE!
];

// NEW: This cleanText PRESERVES markdown for AI insights (Correctly defined here)
const cleanText = (text) =>
  REPLACEMENTS.reduce((acc, [pattern, replacement]) => acc.replace(pattern, replacement), text || '');

  
// --- PageGoal component with plain text ---
function PageGoal({ goal }) {
  return (
    <Text as="p" tone="subdued">
      <Text as="span" fontWeight="bold">{cleanTextNoMarkdown("Page Goal:")}</Text> {cleanTextNoMarkdown(goal)}
    </Text>
  );
}
// --- end PageGoal component ---

// ADDED: Constants for frontend limits
const MAX_SESSIONS_DISPLAY = 30;


export default function SessionsPage() {
  const { token } = useContext(AuthContext);
  const cache = useDataCache();
  const API = process.env.REACT_APP_API_URL || 'https://api.baobrain.com';

  // Quick View Modal state
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickLoading, setQuickLoading] = useState(false);
  const [quickError, setQuickError] = useState(null);
  const [quickEvents, setQuickEvents] = useState([]);

  // Auto-load site_id from sessionStorage
  const [selectedSiteId, setSelectedSiteId] = useState(() => {
    return sessionStorage.getItem('site_id') || '';
  });

  // Today's date is now hardcoded, no state needed
  const selectedDate = useMemo(() => new Date().toISOString().slice(0, 10), []);

  // Fetch sites and set the selectedSiteId
  const { data: sites, loading: sitesLoading, error: sitesError } = useData(
    `sites:${token}`,
    () => fetch(`${API}/api/sites`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
    { ttlMs: 24 * 60 * 60 * 1000, deps: [token, API] }
  );

  // Set the selected site ID once sites are loaded
  useMemo(() => {
    if (sites && sites.length && !selectedSiteId) {
      setSelectedSiteId(String(sites[0].id));
    }
  }, [sites, selectedSiteId]);

  // Use useData for sessions fetching and caching
  const { data: visits, loading: sessionsLoading, error: sessionsError } = useData(
    selectedSiteId ? `sessions:${selectedSiteId}:${selectedDate}` : 'noop',
    () => {
      if (!selectedSiteId) return Promise.resolve([]);
      // MODIFIED: Added limit to the URL
      const url = `${API}/sessions?site_id=${selectedSiteId}&date=${selectedDate}&limit=${MAX_SESSIONS_DISPLAY}`;
      return fetch(url, {
        headers: { Accept: 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
      }).then(res => res.ok ? res.json() : Promise.reject(res.statusText));
    },
    { ttlMs: 60 * 60 * 1000, deps: [API, token, selectedSiteId, selectedDate] }
    //{ ttlMs: 0, deps: [API, token, selectedSiteId, selectedDate] }

  );

  const { data: insights, loading: insightsLoading, error: insightsError } = useData(
    selectedSiteId ? `sessionsAI:${selectedSiteId}:${selectedDate}` : 'noop',
    () => {
      if (!selectedSiteId) return Promise.resolve('');
      return fetch(`${API}/sessions/ai-summary?site_id=${selectedSiteId}&date=${selectedDate}`, {
        headers: { Accept: 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
      }).then(res => res.ok ? res.json() : Promise.reject(res.statusText)).then(json => json.summary || '');
    },
    { 
     ttlMs: 24 * 60 * 60 * 1000,
      //ttlMs: 0 ,
      deps: [], // Empty - don't refetch when anything changes
      refetchOnMount: false, // Don't refetch on mount
      refetchOnWindowFocus: false, // Don't refetch on focus
    }
  );

  // Fix: Memoize formatDuration with useCallback
  const formatDuration = useCallback((sec) => {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    // FIXED: Use cleanTextNoMarkdown for duration display
    return cleanTextNoMarkdown(`${m}m ${s}s`);
  }, []);
// Emphasize important labels inside AI text (rendered via Markdown)

// Also update the emphasizeInsightLabels function (around line 95)
const emphasizeInsightLabels = useCallback((text = '') => {
  return String(text)
    // 1) Intro line — bold the phrase and DO NOT re-add ':' or '.'
    .replace(
      /(The primary (?:revenue opportunity|conversion blocker)(?: this week)? is)[:.]?\s*/i,
      '**$1** '
    )

    // 2) Section headers — match at start-of-line OR after a newline, strip '.'/':'
    .replace(
      /(^|\n)\s*(Key Insight|What We Found|Why It Matters|Recommendations)\s*[:.]\s*/gi,
      '\n\n**$2**\n'
    )

    // 3) Safety pass — if a bolded header still has punctuation, remove it
    .replace(
      /\*\*(Key Insight|What We Found|Why It Matters|Recommendations)\*\*\s*[.:]\s*/gi,
      '**$1**\n'
    )

    // 4) Common inline labels you want as "Label: value"
    .replace(/\b(Data quality)[\s.:]+/i, '**$1:** ')
    .replace(/\b(Status)[\s.:]+/i, '**$1:** ')
    .replace(/\b(Cart abandonment)[\s.:]+/i, '**$1:** ')
    .replace(/\b(Checkout completion)[\s.:]+/i, '**$1:** ')
    .replace(/\b(Bounce rate)[\s.:]+/i, '**$1:** ')
    .replace(/\b(Mobile traffic)[\s.:]+/i, '**$1:** ')
    .replace(/\b(Repeat purchase rate)[\s.:]+/i, '**$1:** ')
    .replace(/\b(High-value customers)[\s.:]+/i, '**$1:** ')
    .replace(/\b(Customer retention)[\s.:]+/i, '**$1:** ');
}, []);


// ============================================================
// FIXED: parsedInsights - handles BOTH old and new formats
// ============================================================
const parsedInsights = useMemo(() => {
  if (!insights) return null;

  const result = {
    intro: '',
    keyInsight: '',
    supportingSignals: [],
    recommendations: [],
    whyItMatters: '',
    nextStep: '',
    note: '',
  };

  const source = String(insights).trim();
  
  if (!source) return result;

  // Check if this is the NEW short format (starts with **)
  const isNewFormat = source.startsWith('**');

  if (isNewFormat) {
    // ============ NEW FORMAT PARSING ============
    const lines = source.split('\n');
    
    // Line 1: **Headline** -> intro
    if (lines[0]) {
      result.intro = lines[0].replace(/\*\*/g, '').trim();
    }
    
    // Find context line -> keyInsight (first non-empty, non-bullet, non-header line after headline)
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line && 
          !line.startsWith('-') && 
          !line.match(/^\d+\./) &&
          !line.toLowerCase().includes(':') &&
          !line.startsWith('**')) {
        result.keyInsight = line;
        break;
      }
    }
    
    // Parse bullets -> supportingSignals
    // Parse numbered items -> recommendations
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      // Bullets go to supportingSignals
      if (line.startsWith('-')) {
        const cleaned = line.replace(/^-\s*/, '').trim();
        if (cleaned) result.supportingSignals.push(cleaned);
      }
      
      // Numbered items go to recommendations
      if (line.match(/^\d+\.\s/)) {
        const cleaned = line.replace(/^\d+\.\s*/, '').trim();
        if (cleaned) result.recommendations.push(cleaned);
      }
    }
    
    // Check for "Top priority:" as a recommendation if none found
    const priorityMatch = source.match(/Top priority:\s*(.+)/i);
    if (priorityMatch && result.recommendations.length === 0) {
      result.recommendations.push(priorityMatch[1].trim());
    }
    
    // Check for "Biggest leak:" and add to supportingSignals
    const leakMatch = source.match(/Biggest leak:\s*(.+)/i);
    if (leakMatch) {
      result.supportingSignals.push(`Biggest leak: ${leakMatch[1].trim()}`);
    }
    
  } else {
    // ============ OLD FORMAT PARSING (unchanged) ============
    
    // 1. Extract intro (The primary revenue/conversion...)
    const introMatch = source.match(/^(The primary (?:revenue opportunity|conversion blocker)[^]*?)(?=\n(?:Key Insight|What We Found|Why It Matters|Recommendations?)[\s.:])/im);
    if (introMatch) {
      result.intro = introMatch[1].trim();
    }

    // 2. Extract Key Insight
    const keyInsightMatch = source.match(/Key Insight[\s.:]+([^]*?)(?=\n(?:What We Found|Why It Matters|Recommendations?)[\s.:]|$)/i);
    if (keyInsightMatch) {
      result.keyInsight = keyInsightMatch[1].trim();
    }

    // 3. Extract What We Found
    const whatWeFoundMatch = source.match(/What We Found[\s.:]+([^]*?)(?=\n(?:Why It Matters|Recommendations?)[\s.:]|$)/i);
    if (whatWeFoundMatch) {
      const lines = whatWeFoundMatch[1].trim().split('\n');
      lines.forEach((line) => {
        const cleaned = line.trim().replace(/^[-*•]\s*/, '');
        if (cleaned) {
          result.supportingSignals.push(cleaned);
        }
      });
    }

    // 4. Extract Why It Matters
    const whyItMattersMatch = source.match(/Why It Matters[\s.:]+([^]*?)(?=\nRecommendations?[\s.:]|$)/i);
    if (whyItMattersMatch) {
      result.whyItMatters = whyItMattersMatch[1].trim();
    }

    // 5. Extract Recommendations
    const recommendationsMatch = source.match(/Recommendations?[\s.:]+([^]*?)(?=\n(?:Session Metrics|Note)[\s.:]|$)/i);
    if (recommendationsMatch) {
      const recText = recommendationsMatch[1].trim();
      const recLines = recText.match(/\d+\.\s*[^]*?(?=\n\d+\.|$)/g);
      if (recLines) {
        recLines.forEach(line => {
          const cleaned = line.replace(/^\d+\.\s*/, '').trim();
          if (cleaned) {
            result.recommendations.push(cleaned);
          }
        });
      }
    }
  }

  return result;
}, [insights]);

  // Quick View Modal helpers
  const openQuickView = useCallback(async (sessionId) => {
    setQuickOpen(true);
    setQuickLoading(true);
    setQuickError(null);
    
    try {
      // Use cache.use() - will fetch if not cached, return cached if available
      const events = await cache.use(
        `sessionDetail:${sessionId}`,
        async () => {
          const url = `${API}/sessions/${encodeURIComponent(sessionId)}?site_id=${encodeURIComponent(selectedSiteId)}`;
          const res = await fetch(url, {
            headers: {
              Accept: 'application/json',
              ...(token && { Authorization: `Bearer ${token}` }),
            },
          });
          if (!res.ok) throw new Error(`Error ${res.status}`);
          return res.json();
        },
        { ttlMs: 60 * 60 * 1000} // Cache for 5 minutes
      );
      
      setQuickEvents(Array.isArray(events) ? events : []);
    } catch (e) {
      setQuickError(cleanTextNoMarkdown(e.message || "Failed to load session details"));
      setQuickEvents([]);
    } finally {
      setQuickLoading(false);
    }
  }, [API, token, selectedSiteId, cache]);


  const closeQuickView = useCallback(() => {
    setQuickOpen(false);
    setQuickEvents([]);
  }, []);

  // Fix: The original fmt function was causing a lint warning.
  // We've moved it to a useCallback hook to make it stable, which prevents unnecessary re-renders.
  // FIXED: Use cleanTextNoMarkdown for UI elements
  const fmt = useCallback((s) => cleanTextNoMarkdown(`${Math.floor(s / 60)}m ${Math.floor(s % 60)}s`), []);

  // Moved memoized hooks to the top level, as they are not conditional
  const quickPageBreakdown = useMemo(() => {
    const evts = quickEvents || [];
    if (!evts.length) return [];
    const pvs = evts.filter(e => e.type === 'pageview').sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    const map = {};
    for (let i = 0; i < pvs.length; i++) {
      const cur = pvs[i], next = pvs[i + 1];
      const page = cur.page || 'unknown';
      const start = new Date(cur.timestamp).getTime();
      const end = next ? new Date(next.timestamp).getTime() : new Date(evts[evts.length - 1].timestamp).getTime();
      if (!map[page]) map[page] = { visits: 0, clicks: 0, scrolls: 0, ms: 0 };
      map[page].visits += 1;
      map[page].ms += Math.max(0, end - start);
    }
    evts.forEach(e => {
      const p = e.page || 'unknown';
      if (!map[p]) return;
      if (e.type === 'click') map[p].clicks += 1;
      if (e.type === 'scroll_depth') map[p].scrolls += 1;
    });
    return Object.entries(map).map(([page, d]) => {
      const secs = Math.floor(d.ms / 1000);
      // FIXED: Use cleanTextNoMarkdown for UI elements
      return [cleanTextNoMarkdown(page), d.visits, d.clicks, d.scrolls, fmt(secs)];
    });
  }, [quickEvents, fmt]);

  const quickGroupedEvents = useMemo(() => {
    const out = {};
    (quickEvents || [])
      .filter(e => !(e.type === 'page_duration' && !e.final)) // hide heartbeats
      .forEach(e => {
        const k = new Date(e.timestamp).toLocaleString();
        if (!out[k]) out[k] = [];
        out[k].push(e);
      });
    return Object.entries(out)
      .sort((a, b) => new Date(a[0]) - new Date(b[0]))
      .map(([ts, arr]) => [
        // FIXED: Use cleanTextNoMarkdown for UI elements
        cleanTextNoMarkdown(ts), 
        cleanTextNoMarkdown(arr[0].type), 
        cleanTextNoMarkdown(arr[0].page || ''), 
        cleanTextNoMarkdown(arr[0].description || '')
      ]);
  }, [quickEvents]);

  // Helpers: normalize backend field names and coerce to numbers
  const getDurationSec = (v) => {
    const raw =
      v.duration_seconds ??
      v.duration_sec ??
      v.duration ??
      v.total_seconds ??
      (v.duration_ms != null ? v.duration_ms / 1000 : null);
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  };
  const getPageviews = (v) => {
    const raw =
      v.pageviews ??
      v.pageViews ??
      v.page_views ??
      v.pages ??
      v.page_count ??
      v.pageview_count ??
      (v.event_counts && v.event_counts.pageview);
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  };

  const totalSessions = Array.isArray(visits) ? visits.length : 0;
  const avgDuration = totalSessions
    ? visits.reduce((sum, v) => sum + getDurationSec(v), 0) / totalSessions
    : 0;
  const avgPageviews = totalSessions
    ? visits.reduce((sum, v) => sum + getPageviews(v), 0) / totalSessions
    : 0;

  // Memoized rows for DataTable
  const allSessionsRows = useMemo(() => {
    // Check if visits is null or undefined, and provide a default empty array
    if (!Array.isArray(visits)) return [];

    return visits.map((v, i) => {
      const id = v.id || v.sessionId || v.session_id;
      // FIXED: Use cleanTextNoMarkdown for UI label
      const label = cleanTextNoMarkdown(`#${i + 1}`);
      const dur = formatDuration(getDurationSec(v));
      return [
        label,
        // FIXED: Use cleanTextNoMarkdown for UI elements
        cleanTextNoMarkdown(v.entryPage || v.entry_page || '-'),
        cleanTextNoMarkdown(v.exitPage || v.exit_page || '-'),
        dur,
        // FIXED: Use cleanTextNoMarkdown for Button text
        <Button key={id} plain size="slim" onClick={() => openQuickView(id)}>{cleanTextNoMarkdown("View")}</Button>,
      ];
    });
  }, [visits, formatDuration, openQuickView]);


  // Memoized scatter data (TOP LEVEL, not inside JSX)
// NEW: (around line 395)
  // Memoized scatter data (TOP LEVEL, not inside JSX)
  const scatterData = useMemo(() => {
    // Check if visits is not an array OR is falsy
    if (!Array.isArray(visits)) return [];

    return visits.map(v => ({
      duration: getDurationSec(v),
      pageviews: getPageviews(v),
    }));
  }, [visits]);


  // Memoized scatter chart
  const ScatterChartMemo = React.memo(function ScatterChartMemo({ data }) {
    return (
      <ResponsiveContainer width="100%" height={300}>
        <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
          <defs>
            <linearGradient id="brandGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="var(--accent)" />
              <stop offset="100%" stopColor="var(--accent-2)" />
            </linearGradient>
          </defs>
          <CartesianGrid strokeOpacity={0.25} />
          <XAxis
            type="number"
            dataKey="duration"
            name="Duration"
            unit="s"
            // FIXED: Use cleanTextNoMarkdown for chart label
            label={{ value: cleanTextNoMarkdown('Duration (s)'), position: 'insideBottom', offset: -10 }}
            tickLine={false}
          />
          <YAxis
            type="number"
            dataKey="pageviews"
            name="Pageviews"
            // FIXED: Use cleanTextNoMarkdown for chart label
            label={{ value: cleanTextNoMarkdown('Pageviews'), angle: -90, position: 'insideLeft' }}
            tickLine={false}
          />
          <RechartsTooltip cursor={{ strokeDasharray: '3 3' }} />
          {/* FIXED: Use cleanTextNoMarkdown for chart name */}
          <Scatter name={cleanTextNoMarkdown("Sessions")} data={data} fill="url(#brandGrad)" shape="circle" />
        </ScatterChart>
      </ResponsiveContainer>
    );
  });

  // --- Start of conditional rendering ---

  // Loading UI
  if (sitesLoading) {
    return (
      <LayoutWrapper>
        {/* FIXED: Use cleanTextNoMarkdown for titles */}
        <Page title={cleanTextNoMarkdown("Visitor Journeys")}>
          <SkeletonPage title={cleanTextNoMarkdown("Loading Sites...")}><SkeletonBodyText lines={2} /></SkeletonPage>
        </Page>
      </LayoutWrapper>
    );
  }

  // General error for initial site fetch
  if (sitesError) {
    return (
      <LayoutWrapper>
        {/* FIXED: Use cleanTextNoMarkdown for titles */}
        <Page title={cleanTextNoMarkdown("Visitor Journeys")}>
          <Layout>
            <Layout.Section>
              {/* FIXED: Use cleanTextNoMarkdown for Banner content */}
              <Banner status="critical">{cleanTextNoMarkdown(`Error loading sites: ${sitesError}`)}</Banner>
            </Layout.Section>
          </Layout>
        </Page>
      </LayoutWrapper>
    );
  }

  // No site selected
  if (!selectedSiteId) {
    return (
      <LayoutWrapper>
        {/* FIXED: Use cleanTextNoMarkdown for titles */}
        <Page title={cleanTextNoMarkdown("Visitor Journeys")}>
          <Layout>
            <Layout.Section>
              <Card sectioned>
                <Banner status="warning">
                  {/* FIXED: Use cleanTextNoMarkdown for Banner content */}
                  {cleanTextNoMarkdown("No site ID found in session. Open a site dashboard first (so it sets sessionStorage.site_id), then return to Sessions.")}
                </Banner>
                <Box paddingBlockStart="400">
                  {/* FIXED: Use cleanTextNoMarkdown for Button text */}
                  <Button onClick={() => window.location.reload()}>{cleanTextNoMarkdown("Reload")}</Button>
                </Box>
              </Card>
            </Layout.Section>
          </Layout>
        </Page>
      </LayoutWrapper>
    );
  }

  // Loading indicator for sessions/insights fetch after filters are applied
  if (sessionsLoading || insightsLoading) {
    return (
      <LayoutWrapper>
        {/* FIXED: Use cleanTextNoMarkdown for titles */}
        <Page title={cleanTextNoMarkdown("Visitor Journeys")}>
          <SkeletonPage title={cleanTextNoMarkdown("Loading Sessions Data...")}><SkeletonBodyText lines={5} /></SkeletonPage>
        </Page>
      </LayoutWrapper>
    );
  }

  return (
    <LayoutWrapper>
      <Page
        // FIXED: Use cleanTextNoMarkdown for titles
        title={cleanTextNoMarkdown("Visitor Journeys")}
        backAction={{
          // FIXED: Use cleanTextNoMarkdown for button content
          content: cleanTextNoMarkdown('Back'),
          onAction: () => window.history.back(),
          icon: ArrowLeftIcon,
        }}
        // ADDED: Time Range Selector as secondaryAction
        secondaryActions={[  
          {    
            content: 'Last 7 Days',    
            onAction: () => {      
              // For now, just keep as static since your data is hardcoded to today    
            },  
          }
        ]}
      >
        {/* PageGoal component is fixed above */}
        <PageGoal goal={"Reconstruct real sessions to spot friction, confusion, and dead ends so your site flows smoothly."} />
        <div style={{
          height: 4,
          width: '100%',
          borderRadius: 4,
          margin: '4px 0 12px',
          background: 'linear-gradient(90deg, var(--accent), var(--accent-2))'
        }} />
        {/* Prescription Banner for conversion issues */}
        <PrescriptionBanner 
          siteId={selectedSiteId}
          token={token}
          category="product_performance"
        />

        <Layout>
          {/* Revenue Impact Summary */}
          <Layout.Section>
            {/* FIXED: Use cleanTextNoMarkdown for Card title */}
            <Card title={cleanTextNoMarkdown("Your Visitors at a Glance")} sectioned>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                <div style={{
                  padding: 20,
                  borderRadius: 12,
                  background: 'rgba(106, 13, 173, 0.1)',
                  border: '1px solid rgba(106, 13, 173, 0.3)'
                }}>
                  <div style={{ fontSize: 24, fontWeight: 'bold', color: 'var(--accent)', marginBottom: 4 }}>
                    {/* FIXED: Use cleanTextNoMarkdown for metric value */}
                    {cleanTextNoMarkdown(totalSessions.toLocaleString())}
                  </div>
                  {/* FIXED: Use cleanTextNoMarkdown for text */}
                  <Text variant="bodyMd" as="h3">{cleanTextNoMarkdown("Total Sessions")}</Text>
                  <Text variant="bodySm" color="subdued">{cleanTextNoMarkdown("Tracked for the selected date")}</Text>
                </div>
                <div style={{
                  padding: 20,
                  borderRadius: 12,
                  background: 'rgba(0, 168, 169, 0.1)',
                  border: '1px solid rgba(0, 168, 169, 0.3)'
                }}>
                  <div style={{ fontSize: 24, fontWeight: 'bold', color: 'var(--accent-2)', marginBottom: 4 }}>
                    {formatDuration(avgDuration)}
                  </div>
                  {/* FIXED: Use cleanTextNoMarkdown for text */}
                  <Text variant="bodyMd" as="h3">{cleanTextNoMarkdown("Avg. Duration")}</Text>
                  <Text variant="bodySm" color="subdued">{cleanTextNoMarkdown("Average time per session")}</Text>
                </div>
                <div style={{
                  padding: 20,
                  borderRadius: 12,
                  background: 'rgba(106, 13, 173, 0.1)',
                  border: '1px solid rgba(106, 13, 173, 0.3)'
                }}>
                  <div style={{ fontSize: 24, fontWeight: 'bold', color: 'var(--accent)', marginBottom: 4 }}>
                    {/* FIXED: Use cleanTextNoMarkdown for metric value */}
                    {cleanTextNoMarkdown(avgPageviews.toFixed(1))}
                  </div>
                  {/* FIXED: Use cleanTextNoMarkdown for text */}
                  <Text variant="bodyMd" as="h3">{cleanTextNoMarkdown("Avg. Pageviews")}</Text>
                  <Text variant="bodySm" color="subdued">{cleanTextNoMarkdown("Average pages per session")}</Text>
                </div>
              </div>
            </Card>
          </Layout.Section>
          {/* Charts Section */}
          <Layout.Section>
            <div className="grid grid-2">
              <Card sectioned>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {/* FIXED: Use cleanTextNoMarkdown for title */}
                  <h3 className="chart-title">{cleanTextNoMarkdown("Session Duration vs. Pageviews")}</h3>
                  {/* FIXED: Use cleanTextNoMarkdown for Tooltip content */}
                  <Tooltip content={cleanTextNoMarkdown("This scatter plot shows the relationship between how long a session lasts and how many pages were viewed. Helps identify patterns in engagement.")}>
                    <Icon source={QuestionCircleIcon} color="subdued" />
                  </Tooltip>
                </div>
                <Box padding="4" className="card--brand-purple">
                  {scatterData.length > 0 ? (
                    <div style={{ height: 320, width: '100%' }}>
                      <ScatterChartMemo data={scatterData} />
                    </div>
                  ) : (
                    // FIXED: Use cleanTextNoMarkdown for text
                    <Text variant="bodyMd" color="subdued" alignment="center">
                      {cleanTextNoMarkdown("No session data available")}
                    </Text>
                  )}
                </Box>
              </Card>

              <Card sectioned>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {/* FIXED: Use cleanTextNoMarkdown for title */}
                  <h3 className="chart-title">{cleanTextNoMarkdown("All Sessions")}</h3>
                  {/* FIXED: Use cleanTextNoMarkdown for Tooltip content */}
                  <Tooltip content={cleanTextNoMarkdown("Each row represents a visitor session, showing entry page, exit page, duration, and a link to detailed session playback.")}>
                    <Icon source={QuestionCircleIcon} color="subdued" />
                  </Tooltip>
                </div>
                <Box padding="4" className="card--brand-teal">
                  {/* FIXED: Use cleanTextNoMarkdown for Banner content */}
                  {sessionsError && <Banner status="critical">{cleanTextNoMarkdown(sessionsError)}</Banner>}
                  {visits?.length > 0 ? (
                    <Scrollable shadow style={{ maxHeight: 300, overflowY: 'auto' }}>
                      <DataTable
                        columnContentTypes={['text', 'text', 'text', 'text', 'button']}
                        headings={[
                          // FIXED: Use cleanTextNoMarkdown for headers
                          cleanTextNoMarkdown('#'), 
                          cleanTextNoMarkdown('Entry'), 
                          cleanTextNoMarkdown('Exit'), 
                          cleanTextNoMarkdown('Duration'), 
                          cleanTextNoMarkdown('Action')
                        ]}
                        rows={allSessionsRows}
                      />
                    </Scrollable>
                  ) : (
                    // FIXED: Use cleanTextNoMarkdown for text
                    <Text alignment="center" as="p" color="subdued">
                      {cleanTextNoMarkdown("No sessions found for the selected date and site.")}
                    </Text>
                  )}
                </Box>
              </Card>
            </div>
          </Layout.Section>

          {/* AI Insights Section - UPDATED LAYOUT */}
<Layout.Section>
  <Card sectioned>
    <div style={{ marginBottom: 20 }}>
      {/* FIXED SPACE BEFORE BRACE: Use cleanTextNoMarkdown for title */}
      <Text variant="heading2xl" as="h2">{cleanTextNoMarkdown("Journey Insights")}</Text>
      {/* FIXED: Use cleanTextNoMarkdown for text */}
      <Text variant="bodyMd" color="subdued">
        {cleanTextNoMarkdown("Behavior patterns from today's visitors")}
      </Text>
    </div>

    {insightsError ? (
      // FIXED: Use cleanTextNoMarkdown for Banner content
  <Banner status="critical">{cleanTextNoMarkdown(`Insights Error: ${insightsError}`)}</Banner>
) : parsedInsights ? (
  <div style={{
    padding: 24,
    borderRadius: 16,
    background: 'linear-gradient(135deg, rgba(106, 13, 173, 0.05), rgba(0, 168, 169, 0.05))',
    border: '2px solid rgba(106, 13, 173, 0.15)'
  }}>
    {/* FIX FOR ISSUE 1: DEBUG BOX ADDED */}
    
    {parsedInsights.intro && (
    <Box paddingBlockEnd="400">
      <ReactMarkdown>{cleanText(emphasizeInsightLabels(parsedInsights.intro))}</ReactMarkdown>
    </Box>
)}

{parsedInsights.keyInsight && (
  <Box paddingBlockEnd="400">
    {/* MODIFIED: Removed period from title */}
    <Text variant="headingLg" as="h3" fontWeight="bold" style={{ color: '#6a0dad', marginBottom: 12 }}>
      {cleanTextNoMarkdown("Key Insight")}
    </Text>
    <ReactMarkdown>
      {cleanText(emphasizeInsightLabels(parsedInsights.keyInsight))}
    </ReactMarkdown>
  </Box>
)}

{parsedInsights.supportingSignals.length > 0 && (
  <Box paddingBlockEnd="400">
    {/* MODIFIED: Removed period from title */}
    <Text variant="headingLg" as="h3" fontWeight="bold" style={{ color: '#6a0dad', marginBottom: 12 }}>
      {cleanTextNoMarkdown("What We Found")}
    </Text>
    <List type="bullet">
      {parsedInsights.supportingSignals.map((signal, idx) => (
        <List.Item key={idx}>
          <ReactMarkdown components={{p: ({children}) => <span>{children}</span>}}>
            {cleanText(emphasizeInsightLabels(signal))}
          </ReactMarkdown>
        </List.Item>
      ))}
    </List>
  </Box>
)}

{parsedInsights.whyItMatters && (
  <Box paddingBlockEnd="400">
    {/* MODIFIED: Removed period from title */}
    <Text variant="headingLg" as="h3" fontWeight="bold" style={{ color: '#6a0dad', marginBottom: 12 }}>
      {cleanTextNoMarkdown("Why It Matters")}
    </Text>
    <ReactMarkdown>
      {cleanText(emphasizeInsightLabels(parsedInsights.whyItMatters))}
    </ReactMarkdown>
  </Box>
)}

{parsedInsights.recommendations.length > 0 && (
  <Box paddingBlockEnd="400">
    {/* MODIFIED: Removed period from title */}
    <Text variant="headingLg" as="h3" fontWeight="bold" style={{ color: '#00a8a9', marginBottom: 12 }}>
      {cleanTextNoMarkdown("Recommendations")}
    </Text>
    <List type="number">
      {parsedInsights.recommendations.map((rec, idx) => (
        <List.Item key={idx}>
          <ReactMarkdown components={{p: ({children}) => <span>{children}</span>}}>{cleanText(rec)}</ReactMarkdown>
        </List.Item>
      ))}
    </List>
  </Box>
)}

    {parsedInsights.note && (
      <Box paddingBlockStart="400" style={{ 
        padding: 16, 
        background: 'rgba(106, 13, 173, 0.05)', 
        borderRadius: 8,
        borderLeft: '4px solid #6a0dad'
      }}>
        {/* FIXED: Use cleanTextNoMarkdown for text */}
        {/* APPLY cleanTextNoMarkdown to the note field to ensure no markdown remains in the subdued text */}
        <Text variant="bodySm" color="subdued">{cleanTextNoMarkdown(cleanText(parsedInsights.note))}</Text>
      </Box>
    )}
  </div>
) : (
  <div style={{ textAlign: 'center', padding: 40 }}>
    {/* FIXED: Use cleanTextNoMarkdown for text */}
    <Text variant="bodyLg" color="subdued">
      {cleanTextNoMarkdown("No visitor data yet. Insights will appear once you have traffic.")}
    </Text>
  </div>
)}
  </Card>
</Layout.Section>


        </Layout>
      </Page>

      <Modal
        open={quickOpen}
        onClose={closeQuickView}
        // FIXED: Use cleanTextNoMarkdown for modal title
        title={cleanTextNoMarkdown("Session Overview")}
        large
        primaryAction={{ 
          // FIXED: Use cleanTextNoMarkdown for button content
          content: cleanTextNoMarkdown('Close'), onAction: closeQuickView 
        }}
      >
        <Modal.Section>
          {quickLoading ? (
            // FIXED: Use cleanTextNoMarkdown for accessibilityLabel
            <InlineStack align="center"><Spinner accessibilityLabel={cleanTextNoMarkdown("Loading")} size="large" /></InlineStack>
          ) : quickError ? (
            // FIXED: Use cleanTextNoMarkdown for Banner content (note: quickError is already cleaned above)
            <Banner status="critical">{quickError}</Banner>
          ) : quickEvents.length === 0 ? (
            // FIXED: Use cleanTextNoMarkdown for text
            <Text tone="subdued">{cleanTextNoMarkdown("No events for this session.")}</Text>
          ) : (
            <>
              {/* FIXED: Use cleanTextNoMarkdown for heading */}
              <Text variant="headingMd">{cleanTextNoMarkdown("Page Breakdown")}</Text>
              <Box paddingBlockStart="2" />
              <DataTable
                columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'text']}
                headings={[
                  // FIXED: Use cleanTextNoMarkdown for headers
                  cleanTextNoMarkdown('Page'), 
                  cleanTextNoMarkdown('Visits'), 
                  cleanTextNoMarkdown('Clicks'), 
                  cleanTextNoMarkdown('Scrolls'), 
                  cleanTextNoMarkdown('Duration')
                ]}
                rows={quickPageBreakdown}
              />
              <Box paddingBlockStart="4" />
              <Divider />
              <Box paddingBlockStart="4" />
              {/* FIXED: Use cleanTextNoMarkdown for heading */}
              <Text variant="headingMd">{cleanTextNoMarkdown("Events")}</Text>
              <Box paddingBlockStart="2" />
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text']}
                headings={[
                  // FIXED: Use cleanTextNoMarkdown for headers
                  cleanTextNoMarkdown('Timestamp'), 
                  cleanTextNoMarkdown('Event Type'), 
                  cleanTextNoMarkdown('Page'), 
                  cleanTextNoMarkdown('Notes')
                ]}
                rows={quickGroupedEvents}
                verticalAlign="middle"
              />
            </>
          )}
        </Modal.Section>
      </Modal>

    </LayoutWrapper>
  );
}